'use client';
import Image from "next/image";
import Link from "next/link";
import { FaQuoteLeft, FaStar } from "react-icons/fa";

interface TestimonialCardProps {
  description: string;
  author: string;
  rating: number; // number of stars
  avatar?: string;
  logo?: string;
  showBottomBorder?: boolean;
  fadeOut?: boolean;
  mounted?: boolean;
  onReadFull?: () => void;
  projectHref?: string | undefined;
  hasProjectLink?: boolean; // Whether to show the project link
}

const TestimonialCard: React.FC<TestimonialCardProps> = ({
  description,
  author,
  rating,
  avatar,
  logo,
  showBottomBorder = false,
  fadeOut = false,
  onReadFull,
  projectHref,
  hasProjectLink = false,
}) => {
  const displayedCircleImage = avatar || logo || "/test-images/default-avtar.png";
  return (
    <div
      className={`
        border border-[#C5C5C5]/15 p-6  bg-[#e9e9e9]/15 backdrop-blur-sm relative
        ${showBottomBorder ? "border-b" : ""}
       
        transition-all duration-300
      `}
    >
      {/* Top Row: Quote Icon + Avatar */}
      <div className="flex items-start justify-between mb-4">
        <FaQuoteLeft className="text-gray-300 text-3xl" />
        <div
          className={`
    w-[60px] h-[60px] rounded-full overflow-hidden border-2 border-white shadow
    transition-all duration-300
    ${fadeOut ? "opacity-0 translate-y-2 blur-[1px] scale-[0.995]" : "opacity-100 translate-y-0 blur-0 scale-100"}
  `}
          style={{ willChange: "opacity, transform, filter" }}
        >
          <Image
            src={displayedCircleImage}
            alt={author}
            fill
            className="object-cover"
            sizes="60px"
          />
        </div>

      </div>

      {/* Quote removed intentionally */}
      {/* Description */}
      <p className="text-[#1b1b1b] 2xl:text-lg text-sm leading-snug mb-8 line-clamp-2">
        {description}
      </p>

      {/* Rating + Read More */}
      <div className="flex items-center  justify-between text-sm text-gray-800 mb-3 border-b border-[#cdcdcd]/48 pb-3">

        <div className="flex items-center gap-2">
          <span>{rating} / 5</span>
          <div className="flex gap-1 text-orange-400">
            {[...Array(Math.floor(rating))].map((_, idx) => (
              <FaStar key={idx} />
            ))}
          </div>
        </div>
        <p className="font-bold text-sm">{author}</p>
      </div>
      <div className="flex items-center justify-end">
        <div className="flex items-center space-x-4">
          <button
            type="button"
            {...(onReadFull && { onClick: onReadFull })}
            className="text-red-600 hover:text-[#d53333] font-medium text-base cursor-pointer transition-colors"
            aria-label={`Read in full about ${author}`}
          >
            Read in full
          </button>

          {hasProjectLink && projectHref && (
            <>
              <span className="text-gray-400">/</span>
              <Link
                href={projectHref}
                className="text-red-600 hover:text-[#D53333] font-medium text-base cursor-pointer transition-colors"
                aria-label={`Check the project related to ${author}`}
              >
                Check the project
              </Link>
            </>
          )}
        </div>
      </div>
      {/* Author */}

    </div>
  );
};

export default TestimonialCard;
